import React, { useEffect, useRef, useState } from 'react';
import './WhopClickTestSandbox.css';

// Reconstructs the REAL Whop Content Rewards review card markup -- class
// names and structure confirmed live throughout the review-bot build
// (real HTML dumps + screenshots), not guessed. Purpose: let the Chrome
// extension's actual click flow (content-whop.js's clickVerdict()) run
// against something structurally identical to the real dashboard, so the
// full approve/reject-with-reason/submit loop can be proven safe here
// BEFORE it's ever pointed at the real Whop page.
//
// Confirmed real markers this mock reproduces:
//   .btn-accept-container / .btn-reject-container / .btn-flag-container /
//     .btn-warning-container -- action buttons (Ban is rendered but its
//     handler is a no-op here too -- nothing should ever click it)
//   .analytics-section-bg -- section wrappers (title/submitter, stats)
//   .shield-score-text -- Whop's own trust/risk score
//   .pill-neutral-text "Pending" / .pill-success-text "Approved" /
//     .pill-error-text "Rejected" or "Deleted"
//   video[src] pointing at a contentrewards.com-style CDN URL
//   .toast-title-text "Reject this submission?" + 4x .toast-quick-select-btn
//     + a "Give reason" button
//   input[placeholder="Reason for rejection..."] (React-controlled --
//     exercises the native-setter trick content-whop.js relies on)
//   .btn-primary-container "Submit" -- disabled until the input has text

// Real text, confirmed from a full HTML dump of the actual reject toast.
const QUICK_REASONS = [
  "Content doesn't meet campaign requirements",
  'Low quality content',
  'Inappropriate content',
  'Wrong campaign guidelines',
];

function makeCard(overrides = {}) {
  return {
    title: 'James O’Keefe Clips',
    submitter: 'test_submitter',
    payout: '$4.57',
    shieldScore: 40,
    status: 'pending', // pending | approved | rejected | deleted
    videoUrl: 'https://cdn.contentrewards.com/clips/sample-1.mp4',
    originalUrl: 'https://www.tiktok.com/@jamesokeefe.highlights/video/7669158592406211853',
    views: 3044,
    likes: 45,
    comments: 0,
    shares: 2,
    rejectStage: 'none', // none | confirm | reason
    reasonText: '',
    ...overrides,
  };
}

const INITIAL_CARDS = [
  makeCard({
    title: 'They Didn’t Want This To Go Public \u{1F633}', submitter: 'anu_clips',
    videoUrl: 'https://cdn.contentrewards.com/clips/sample-1.mp4',
    originalUrl: 'https://www.tiktok.com/@jamesokeefe.highlights/video/7669158592406211853',
  }),
  makeCard({
    title: 'JO 8', submitter: 'muhammadhashim01', status: 'deleted',
    videoUrl: '', originalUrl: '', views: 4, likes: 1, comments: 0, shares: 0,
  }),
  makeCard({
    title: 'Huge discovery cross-referencing DHS lists', submitter: 'hello_clipp',
    videoUrl: 'https://cdn.contentrewards.com/clips/sample-3.mp4',
    originalUrl: 'https://www.tiktok.com/@hello_clipp/video/7669158592406299999',
  }),
];

const MAX_CARDS = 6;

export default function WhopClickTestSandbox() {
  const [cards, setCards] = useState(INITIAL_CARDS);
  const [log, setLog] = useState([]);
  const dismissTimers = useRef({});
  const loadingMore = useRef(false);

  function update(idx, patch) {
    setCards((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function appendLog(line) {
    setLog((prev) => [...prev, `${new Date().toLocaleTimeString()} — ${line}`]);
  }

  // Real Whop's reject-confirm toast auto-dismisses after ~5s if "Give
  // reason" isn't clicked in time (confirmed, documented in
  // content-whop.js) -- reproduced here so clickVerdict()'s tight
  // pollFor() timing gets tested against a real race instead of a toast
  // that just sits open forever.
  function startDismissTimer(idx) {
    clearTimeout(dismissTimers.current[idx]);
    dismissTimers.current[idx] = setTimeout(() => {
      setCards((prev) => prev.map((c, i) => (i === idx && c.rejectStage === 'confirm' ? { ...c, rejectStage: 'none' } : c)));
      appendLog(`Card ${idx}: reject toast auto-dismissed after 5s (no reason given in time)`);
    }, 5000);
  }

  useEffect(() => {
    const timers = dismissTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Simulated infinite-scroll lazy-loading -- real Whop's submission list
  // loads more cards as you scroll near the bottom, which is exactly what
  // runAllCards() is built to detect and wait for. A static 3-card page
  // never exercises that "scroll, wait, find new cards" loop at all.
  useEffect(() => {
    function onScroll() {
      if (loadingMore.current || cards.length >= MAX_CARDS) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 300;
      if (!nearBottom) return;
      loadingMore.current = true;
      appendLog('Scrolled near bottom -- loading more cards (simulated network delay)...');
      setTimeout(() => {
        setCards((prev) => {
          const n = prev.length + 1;
          return [...prev, makeCard({
            title: `Extra lazy-loaded clip #${n}`,
            submitter: `lazyload_user${n}`,
            videoUrl: `https://cdn.contentrewards.com/clips/sample-lazy-${n}.mp4`,
            originalUrl: `https://www.tiktok.com/@lazyload_user${n}/video/76691585924063${n}000`,
          })];
        });
        loadingMore.current = false;
        appendLog('New card loaded from scroll.');
      }, 800);
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [cards.length]);

  // Exposed for manual poking from the console if needed, and so this
  // page's state is inspectable the same way ClipReviewSandbox exposes
  // window.__clipSandbox.
  window.__whopClickTest = { cards, log };

  // Real Whop shows ONE toast, fixed to the bottom-right corner of the
  // viewport -- not one embedded inline per card. Rendering it inside the
  // cards.map() loop (the original mistake here) meant it would appear
  // wherever that card happened to sit in page flow instead of the corner
  // being watched -- confirmed as the actual cause of "I don't see any
  // popup." Rendered once, globally, for whichever card currently has an
  // active reject flow.
  const activeRejectIdx = cards.findIndex((c) => c.rejectStage !== 'none');
  const activeCard = activeRejectIdx >= 0 ? cards[activeRejectIdx] : null;

  return (
    <div className="whop-click-test-page">
      <div className="wct-header">
        <h1>Whop Click-Test Sandbox</h1>
        <p>
          Real Whop markup, reconstructed from confirmed selectors -- run the extension's
          "Run Clip Review Bot" here to test the actual approve/reject-with-reason/submit
          click flow before it ever touches the real dashboard.
        </p>
      </div>

      {cards.map((card, idx) => (
        <div className="wct-card" key={idx}>
          <div className="analytics-section-bg wct-title-row">
            <div>
              <p className="wct-title">{card.title}</p>
              <button className="wct-submitter-btn" type="button">
                <p>{card.submitter}</p>
              </button>
            </div>
            <div className="wct-actions">
              <button
                className="btn-flag-container"
                type="button"
                title="Flag"
                onClick={() => appendLog(`Card ${idx}: flag clicked (not wired -- expected no-op)`)}
              >
                &#9873;
              </button>
              <button
                className="btn-accept-container"
                type="button"
                disabled={card.status === 'approved' || card.status === 'rejected'}
                onClick={() => {
                  update(idx, { status: 'approved' });
                  appendLog(`Card ${idx} ("${card.title}"): APPROVED`);
                }}
              >
                &#10003; Approve
              </button>
              <button
                className="btn-reject-container"
                type="button"
                disabled={card.status === 'approved' || card.status === 'rejected'}
                onClick={() => {
                  update(idx, { rejectStage: 'confirm' });
                  appendLog(`Card ${idx} ("${card.title}"): reject clicked, confirm toast shown (auto-dismisses in 5s)`);
                  startDismissTimer(idx);
                }}
              >
                &#10005; Reject
              </button>
              <button
                className="btn-warning-container"
                type="button"
                title="Ban"
                onClick={() => appendLog(`⚠️ Card ${idx}: BAN button clicked -- this should NEVER happen from the bot`)}
              >
                Ban
              </button>
            </div>
          </div>

          <div className="wct-meta-row">
            <span>Est. payout {card.payout}</span>
            <span className="shield-score-text">{card.shieldScore}</span>
            {card.status === 'pending' && <span className="pill-neutral-text">Pending</span>}
            {card.status === 'approved' && <span className="pill-success-text">Approved</span>}
            {card.status === 'rejected' && <span className="pill-error-text">Rejected</span>}
            {card.status === 'deleted' && <span className="pill-error-text">Deleted</span>}
          </div>

          {card.status === 'deleted' ? (
            <div className="wct-video-unavailable">
              <p>Video unavailable</p>
              <p>This video has been removed by the uploader</p>
            </div>
          ) : (
            <>
              {card.videoUrl && <video src={card.videoUrl} controls muted />}
              {card.originalUrl && (
                <a href={card.originalUrl} target="_blank" rel="noreferrer">Watch video</a>
              )}
            </>
          )}

          <div className="analytics-section-bg wct-stats-row">
            <button type="button"><p>Views</p><p>{card.views.toLocaleString()}</p></button>
            <button type="button"><p>Likes</p><p>{card.likes.toLocaleString()}</p></button>
            <button type="button"><p>Comments</p><p>{card.comments.toLocaleString()}</p></button>
            <button type="button"><p>Shares</p><p>{card.shares.toLocaleString()}</p></button>
          </div>
        </div>
      ))}

      {/* ONE persistent toast, fixed to the bottom-right corner of the
          viewport -- matches the real Whop toast's actual on-screen
          position. It expands in place (not two separate toasts): the
          header (title + Give reason + Ban + X) stays visible the whole
          time; "Give reason" reveals a section below it (input+Submit AND
          the quick-select chips together), it doesn't replace anything. */}
      {activeCard && (
        <div className="wct-toast">
          <div className="wct-toast-header">
            <span className="toast-icon-warning" aria-hidden="true">&#9888;</span>
            <span className="toast-title-text">Reject this submission?</span>
            <div className="wct-toast-header-actions">
              <button
                className="btn-ghost-container"
                type="button"
                onClick={() => {
                  clearTimeout(dismissTimers.current[activeRejectIdx]);
                  update(activeRejectIdx, { rejectStage: 'reason' });
                  appendLog(`Card ${activeRejectIdx}: "Give reason" clicked in time, toast expanded`);
                }}
              >
                Give reason
              </button>
              <button
                className="btn-warning-container"
                type="button"
                onClick={() => appendLog(`⚠️ Card ${activeRejectIdx}: BAN button clicked (inside reject toast) -- this should NEVER happen from the bot`)}
              >
                Ban
              </button>
              <button
                className="wct-toast-close"
                type="button"
                title="Close"
                onClick={() => {
                  clearTimeout(dismissTimers.current[activeRejectIdx]);
                  update(activeRejectIdx, { rejectStage: 'none' });
                  appendLog(`Card ${activeRejectIdx}: toast closed without rejecting`);
                }}
              >
                &#10005;
              </button>
            </div>
          </div>

          {activeCard.rejectStage === 'confirm' && (
            <div className="wct-toast-countdown-track">
              <div className="wct-toast-countdown-bar" key={activeRejectIdx} />
            </div>
          )}

          {activeCard.rejectStage === 'reason' && (
            <div className="wct-toast-expanded">
              <div className="wct-toast-input-row">
                <input
                  type="text"
                  placeholder="Reason for rejection..."
                  value={activeCard.reasonText}
                  onChange={(e) => update(activeRejectIdx, { reasonText: e.target.value })}
                />
                <button
                  className="btn-primary-container"
                  type="button"
                  disabled={!activeCard.reasonText.trim()}
                  onClick={() => {
                    update(activeRejectIdx, { status: 'rejected', rejectStage: 'none' });
                    appendLog(`Card ${activeRejectIdx} ("${activeCard.title}"): REJECTED, reason submitted: "${activeCard.reasonText}"`);
                  }}
                >
                  Submit
                </button>
              </div>
              <div className="wct-quick-reasons">
                {QUICK_REASONS.map((r) => (
                  <button
                    key={r}
                    className="toast-quick-select-btn"
                    type="button"
                    onClick={() => appendLog(`Card ${activeRejectIdx}: quick-select "${r}" clicked (should never happen -- bot must use "Give reason" + type a specific reason)`)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="wct-log">
        <h2>Click log</h2>
        {log.length === 0 && <p className="wct-log-empty">Nothing yet -- run the extension.</p>}
        {log.map((line, i) => <div key={i}>{line}</div>)}
      </div>
    </div>
  );
}
